import { Form, notification } from 'antd';
import isEqual from 'lodash/isEqual';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyGetBranchesForSystemQuery } from '@app/api/Branches';
import {
  useLazyGetPositionsForSystemQuery,
  useLazyGetPositionsQuery,
  useUpdatePositionMutation,
} from '@app/api/Positions';
import {
  useNormalizeUserPhotoMutation,
  useSetUserPhotoMutation,
  useUpdateUserMutation,
} from '@app/api/User/api';
import { AvatarDropdownItems } from '@app/components';
import type { PhoneValidation } from '@app/components/Phone/types';
import {
  DATE_PICKER_FORMAT,
  ISO_FORMAT_INDEX,
  UNKNOWN_GENDER_ID,
} from '@app/constants';
import { ERROR, FRONT_WARNING, SUCCESS, WARNING } from '@app/constants/alerts';
import {
  ADD_EMPLOYEE,
  DELETE_EMPLOYEE,
} from '@app/constants/updateUserSubmitTypes';
import {
  useNewUserAccessRights,
  useUnsavedData,
  useUserPrint,
} from '@app/hooks';
import type { FormType } from '@app/hooks/useUnsavedData';
import { LeavePageContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NEmployees, NSmallGlobalEntities } from '@app/types';
import type { NBranches } from '@app/types/Branches';
import type { NUsers } from '@app/types/Users';
import type { NUtils } from '@app/types/Utils';
import {
  afterUserUpdate,
  capitalizeKeys,
  keysFirstLetterToLower,
  transformImportedName,
} from '@app/utils';

import { useEditEmployeeUnsavedChanges } from '@pages/Employees/EditEmployee/hooks/useEditEmployeeUnsavedChanges';

import type { AddEmployeeFormValues } from '@pages/Employees/AddEmployee/types';

const { useForm, useWatch } = Form;
export const useEmployeeInfoTab = (
  employee: NEmployees.EditEmployeeInfoTabProps['employee'],
  setEmployee: (props: NEmployees.EditEmployeeInfoTabProps['employee']) => void,
  setIsNeedToUpdateUserPreview: (props: boolean) => void
) => {
  const [isUpdated, setIsUpdated] = useState(false);
  const [isPhoneUpdated, setIsPhoneUpdated] = useState(false);

  const [isInfoLoader, setInfoLoader] = useState(false);
  const [allBranches, setAllBranches] = useState<NBranches.ModelBranchItem[]>(
    []
  );
  const [selectedBranches, setSelectedBranches] = useState<
    NBranches.ModelBranchItem[]
  >([]);
  const [positions, setPositions] = useState<NSmallGlobalEntities.ModelItem[]>(
    []
  );
  const [infoTabError, setInfoTabError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [, setErrorType] = useState<NUtils.ErrorTypes>(undefined);
  const [phoneCountryId, setPhoneCountryId] = useState<
    number | null | undefined
  >(1);

  const [confirmDeletingModalOpen, setConfirmDeletingModalOpen] =
    useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const [, contextHolder] = notification.useNotification();

  const { userId, database, activeBranchId } = useUserPrint();

  const { showNotification } = useContext(NotificationContext);

  const [infoTabForm] = useForm<AddEmployeeFormValues>();
  const [modalForm] = useForm();

  const { prepareStartedData } = useContext(LeavePageContext);

  const navigate = useNavigate();

  const [phoneValidation, setPhoneValidation] = useState<PhoneValidation>({
    isValid: false,
    errorMessage: null,
  });

  const observedData = useWatch([], infoTabForm);

  const { saveData } = useUnsavedData({
    data: observedData as unknown as FormType,
    skip: isInfoLoader || !Boolean(employee),
  });

  const {
    userAccessRights: { IsAdmin },
  } = useNewUserAccessRights();

  const [getBranchesForSystem] = useLazyGetBranchesForSystemQuery();
  const [getPositionsForSystem] = useLazyGetPositionsForSystemQuery();
  const [getPositions] = useLazyGetPositionsQuery();
  const [updatePosition] = useUpdatePositionMutation();
  const [normalizeUserPhoto] = useNormalizeUserPhotoMutation();
  const [setUserPhoto] = useSetUserPhotoMutation();
  const [updateUser] = useUpdateUserMutation();

  useEffect(() => {
    const savedPhone = {
      Phone: employee?.phone || '',
      CountryId: employee?.phoneCountryId || 1,
    };
    const currentPhone = {
      Phone: observedData?.Phone?.Phone || '',
      CountryId: observedData?.Phone?.CountryId || 1,
    };

    setIsPhoneUpdated(!isEqual(savedPhone, currentPhone));
  }, [employee?.phone, employee?.phoneCountryId, observedData?.Phone]);

  useEffect(() => {
    const getData = async () => {
      if (activeBranchId) {
        try {
          const branchesResponse = await getBranchesForSystem({
            ActiveBranchId: activeBranchId,
          }).unwrap();

          setAllBranches(branchesResponse);

          const positionsResponse = await getPositionsForSystem({
            ActiveBranchId: activeBranchId,
          }).unwrap();

          const adaptedPositionsToUi = positionsResponse.map(
            ({ Id, Name }) => ({ Id, Name, IdS: null })
          );

          adaptedPositionsToUi.unshift({
            Id: 0,
            IdS: null,
            Name: 'Без должности',
          });

          setPositions(adaptedPositionsToUi);
        } catch (error) {
          showNotification(handleCatchError(error), WARNING);
        }
      }
    };

    const loadData = async () => {
      setInfoLoader(true);

      try {
        await getData();
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      } finally {
        setInfoLoader(false);
      }
    };

    loadData();
  }, [
    activeBranchId,
    database,
    userId,
    showNotification,
    getBranchesForSystem,
    getPositionsForSystem,
  ]);

  const sendDataToDb = useCallback(
    async ({
      NameFirst,
      NameLast,
      NameMiddle,
      Position,
      Gender,
      Description = '',
      Email,
      Password,
      DateBirth,
      Phone: { Phone, CountryId },
      Note = '',
      Snils,
      SignerRole,
      PositionCode,
      SpecialityCodeNsi,
      TelegramId,
    }: AddEmployeeFormValues) => {
      const { errorMessage, isValid } = phoneValidation;

      const phoneErrorMessage =
        errorMessage || (!isValid ? 'Обязательное поле' : '');

      if (phoneErrorMessage) {
        infoTabForm.setFields([
          {
            name: 'Phone',
            errors: [phoneErrorMessage],
          },
        ]);
        return false;
      }

      const data = {
        NameFirst,
        NameLast: NameLast || null,
        NameMiddle: NameMiddle || null,
        Email: Email || null,
        Password: Password || null,
        Phone,
        PhoneCountryId: CountryId,
        PositionId: Position === 0 ? null : Position,
        Gender:
          Gender === UNKNOWN_GENDER_ID || Gender === undefined ? null : Gender,
        DateBirth: DateBirth
          ? DateBirth.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
          : null,
        Description,
        Note,
        IsEmployee: true,
        IsSaveAdditions: true,
        UserBranchIds: selectedBranches.length
          ? selectedBranches.filter((item) => item !== null).map(({ Id }) => Id)
          : null,
        IsFakePhone: false,
        IsFakeEmail: false,
        Snils,
        SignerRole,
        PositionCode,
        SpecialityCodeNsi,
        TelegramId: TelegramId || null,
      };

      if (!employee) {
        showNotification('Исполнитель не найден', FRONT_WARNING);
        return false;
      }

      setEmployee({
        ...employee,
        ...keysFirstLetterToLower(data),
      });

      if (userId && database && activeBranchId && employee) {
        try {
          setInfoLoader(true);
          await updateUser({
            data: {
              ...capitalizeKeys<NUsers.ModelUser>(employee),
              ...data,
            },
            ActiveBranchId: activeBranchId,
            logs: { isLogged: true, userId, database },
          }).unwrap();

          infoTabForm.resetFields(['Password']);

          showNotification('Пользователь обновлен успешно', SUCCESS);
          prepareStartedData({});
          setIsNeedToUpdateUserPreview(true);
          saveData();
          setIsUpdated(false);
          afterUserUpdate({
            id: employee.id,
            navigate,
            submitType: ADD_EMPLOYEE,
            form: infoTabForm,
            isEditPage: true,
          });
          setInfoTabError('');
          return true;
        } catch (error) {
          setErrorType(ERROR);
          setInfoTabError(handleCatchError(error));
          return false;
        } finally {
          setInfoLoader(false);
        }
      }

      return false;
    },
    [
      selectedBranches,
      setEmployee,
      employee,
      userId,
      database,
      activeBranchId,
      showNotification,
      prepareStartedData,
      setIsNeedToUpdateUserPreview,
      navigate,
      infoTabForm,
      phoneValidation,
      saveData,
      updateUser,
    ]
  );

  const submitCurrentForm = useCallback(async () => {
    try {
      const values = await infoTabForm.validateFields();

      return await sendDataToDb(values);
    } catch {
      return false;
    }
  }, [infoTabForm, sendDataToDb]);

  const hasUnsavedChanges = isUpdated || isPhoneUpdated;

  const { handleUnsavedChanges } = useEditEmployeeUnsavedChanges({
    isUpdated: hasUnsavedChanges,
    setIsUpdated,
    submitCurrentForm,
  });

  const addPosition = () => {
    showModal();
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    modalForm.submit();
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    modalForm.resetFields();
    setIsModalOpen(false);
  };

  const finishModal: NEmployees.FinishModal = async ({ Name, Description }) => {
    try {
      await updatePosition({
        ActiveBranchId: activeBranchId,
        data: {
          Id: 0,
          Name,
          Description,
          IsDeleted: false,
          MigrateId: null,
        },
      }).unwrap();

      showNotification('Должность успешно добавлена', SUCCESS);

      const response = await getPositions({
        ActiveBranchId: activeBranchId,
      }).unwrap();

      const positionsAdaptedToUi = response.map(({ Id, Name }) => ({
        Id,
        Name,
        IdS: null,
      }));
      const updatedPositions = [
        {
          Id: 0,
          IdS: null,
          Name: 'Без должности',
        },
        ...positionsAdaptedToUi,
      ];

      setPositions(positionsAdaptedToUi);

      const position = updatedPositions.find(
        ({ Name: localName }) => localName === Name
      );

      if (position) {
        infoTabForm.setFieldValue('Position', position.Id);
      }
    } catch (error) {
      setInfoTabError(handleCatchError(error));
    }
  };

  const showBranchesModal = () => {
    setOpen(true);
  };

  const hideBranchesModal = () => {
    setOpen(false);
  };

  const deleteEmployee = async () => {
    if (!employee)
      return showNotification(
        'Данные по пользователю не найдены',
        FRONT_WARNING
      );

    const data: NUsers.ModelUser = { ...capitalizeKeys(employee) };
    const birthDate = infoTabForm.getFieldValue('DateBirth');

    data.NameFirst = infoTabForm.getFieldValue('NameFirst');
    data.NameMiddle = infoTabForm.getFieldValue('NameMiddle');
    data.NameLast = infoTabForm.getFieldValue('NameLast');
    data.PhoneCountryId = phoneCountryId || null;
    data.PositionId =
      infoTabForm.getFieldValue('Position') === 0
        ? null
        : infoTabForm.getFieldValue('Position');
    data.Gender =
      infoTabForm.getFieldValue('Gender') === '' ||
      infoTabForm.getFieldValue('Gender') === undefined
        ? null
        : infoTabForm.getFieldValue('Gender');
    data.DateBirth = birthDate
      ? birthDate.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
      : null;
    data.Description = infoTabForm.getFieldValue('Description');
    data.IsEmployee = true;
    data.UserBranchIds = selectedBranches.length
      ? selectedBranches.map(({ Id }) => Id).filter((id) => id !== undefined)
      : null;
    data.Email =
      employee?.isFakeEmail && !infoTabForm.getFieldValue('Email')
        ? null
        : infoTabForm.getFieldValue('Email');
    data.IsDeleted = true;

    if (userId && database && activeBranchId && employee) {
      try {
        setInfoLoader(true);

        await updateUser({
          data: {
            ...data,
          },
          ActiveBranchId: activeBranchId,
          logs: { isLogged: true, userId, database },
        }).unwrap();
        await saveData();
        showNotification('Пользователь удален успешно', SUCCESS);
        prepareStartedData({});
        setIsNeedToUpdateUserPreview(true);

        afterUserUpdate({
          id: employee.id,
          navigate,
          submitType: DELETE_EMPLOYEE,
          form: infoTabForm,
        });
      } catch (error) {
        setErrorType(ERROR);
        setInfoTabError(handleCatchError(error));
      } finally {
        setConfirmDeletingModalOpen(false);
        setInfoLoader(false);
        setConfirmDeletingModalOpen(false);
      }
    }
  };

  const fallbackName = useMemo(() => {
    if (!employee) return;
    if (employee?.photo) return;

    const { nameFirst, nameLast } = employee;

    const combinedName =
      nameLast && nameFirst
        ? `${nameLast} ${nameFirst}`
        : nameLast || nameFirst || '';

    return transformImportedName(combinedName);
  }, [employee]);

  const photoUrl = useMemo(() => {
    if (!employee?.photo) return;

    return `data:image/png;base64,${employee?.photo}`;
  }, [employee?.photo]);

  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = async function () {
        if (typeof reader.result === 'string') {
          const base64String = reader.result
            .replace('data:', '')
            .replace(/^.+,/, '');

          if (!employee?.id) {
            showNotification(
              'Не удалось определить идентификатор пользователя',
              FRONT_WARNING
            );
            return;
          }

          try {
            const { NormalizedPhoto } = await normalizeUserPhoto({
              ActiveBranchId: activeBranchId,
              data: {
                InputImage: base64String,
                InputBytes: null,
                NormalizedPhoto: null,
              },
            }).unwrap();

            const result = await setUserPhoto({
              ActiveBranchId: activeBranchId,
              data: {
                UserId: employee.id,
                PhotoBase64: NormalizedPhoto,
              },
            }).unwrap();

            if (employee) {
              setEmployee({
                ...employee,
                photo: result.PhotoBase64 || '',
              });
            }

            showNotification('Фото установлено', SUCCESS);
          } catch (error) {
            showNotification(handleCatchError(error));
          }
        }
      };
    }
  };

  const deleteAvatar = async () => {
    if (!employee?.id) {
      showNotification(
        'Не удалось определить идентификатор пользователя',
        FRONT_WARNING
      );
      return;
    }

    try {
      const result = await setUserPhoto({
        ActiveBranchId: activeBranchId,
        data: {
          UserId: employee.id,
          PhotoBase64: null,
        },
      }).unwrap();

      if (employee) {
        setEmployee({
          ...employee,
          photo: result.PhotoBase64 || '',
        });
      }

      showNotification('Фото удалено', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  const avatarDropdownItems = AvatarDropdownItems({
    uploadAvatar,
    deleteAvatar,
    Photo: employee?.photo || undefined,
  });

  return {
    infoTabError,
    contextHolder,
    infoTabForm,
    sendDataToDb,
    positions,
    isModalOpen,
    modalForm,
    addPosition,
    handleOk,
    handleCancel,
    finishModal,
    isInfoLoader,
    setInfoTabError,
    open,
    allBranches,
    selectedBranches,
    setSelectedBranches,
    showBranchesModal,
    hideBranchesModal,
    setPhoneCountryId,
    confirmDeletingModalOpen,
    setConfirmDeletingModalOpen,
    deleteEmployee,
    isFirstLoad,
    setIsFirstLoad,
    isUpdated: hasUnsavedChanges,
    setIsUpdated,
    setPhoneValidation,
    phoneValidation,
    handleUnsavedChanges,
    fallbackName,
    photoUrl,
    avatarDropdownItems,
    IsAdmin,
  };
};
